import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SectionLayout from '@/Layout/SectionLayout';
import TextLink from '@/components/TextLink';
import usePlatform from '@/hooks/use-platform';
import useTextHandler from '@/hooks/use-text-handler';
import Button from '@/shared/components/Button';
import { type TotpBindingState } from '@/types/guard';

import styles from './index.module.scss';

/** Format secret in groups of 4 characters for readability (Keycloak TotpUtils pattern) */
const formatSecret = (raw: string): string => raw.replace(/(.{4})/g, '$1 ').trim();

const SecretSection = ({ secret, secretQrCode, secretKeyUri }: TotpBindingState) => {
  const { t } = useTranslation();
  const { isMobile } = usePlatform();
  const [showQrCode, setShowQrCode] = useState(!isMobile);
  const { copyText } = useTextHandler();

  return (
    <SectionLayout
      title="mfa.step"
      titleProps={{
        step: 1,
        content: t(showQrCode ? 'mfa.scan_qr_code' : 'mfa.copy_and_paste_key'),
      }}
      description={
        showQrCode ? 'mfa.scan_qr_code_description' : 'mfa.copy_and_paste_key_description'
      }
    >
      <div className={styles.secretContent}>
        {showQrCode && secretQrCode && (
          <div className={styles.qrCode}>
            <img data-testid="mfa_image_qr_code" src={secretQrCode} alt="QR code" />
          </div>
        )}
        {!showQrCode && (
          <div className={styles.copySecret}>
            <div data-testid="mfa_text_raw_secret" className={styles.rawSecret}>{formatSecret(secret)}</div>
            <Button
              data-testid="mfa_button_copy_secret"
              title="action.copy"
              type="secondary"
              onClick={() => {
                void copyText(secret, t('mfa.secret_key_copied'));
              }}
            />
          </div>
        )}
        <TextLink
          data-testid="mfa_link_toggle_secret_view"
          text={showQrCode ? 'mfa.qr_code_not_available' : 'mfa.want_to_scan_qr_code'}
          onClick={() => {
            setShowQrCode(!showQrCode);
          }}
        />
      </div>
    </SectionLayout>
  );
};

export default SecretSection;
